import React, { useContext, useState } from 'react'

import { Header, Button, Modal, Form, Message, Divider } from 'semantic-ui-react'

import { IProject } from '../../../api/Data'
import { UserContext } from '../../../contexts/UserContext'

import ExternalLink from '../../../components/common/ExternalLink'
import UpgradeTag from '../../../components/common/UpgradeTag'

import ClassicUploaderPreview, { PreviewMode } from './ClassicUploaderPreview'

const ConsentStatusRow = ({ label, enabled }: { label: string; enabled: boolean }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.75rem',
      marginBottom: '0.35rem',
      fontSize: '12px',
    }}
  >
    <span>{label}</span>
    <strong>{enabled ? '✓' : '✘'}</strong>
  </div>
)
const CollectTabLandingPagePreview = ({
  project,
  handleChange,
}: {
  project: IProject
  handleChange?: (
    _event: unknown,
    data: { name: string; value: any; checked?: boolean; projectChanges?: Partial<IProject> }
  ) => void
}) => {
  // Contexts
  const { userProfile } = useContext(UserContext)

  // Props
  const {
    rules,
    teamTermsUrl,
    locale,
    useTeamLegalSettings,
    overrideContentOwner,
    overridePrivacyUrl,
    overrideLegalDisclosureUrl,
    additionalDeclarationEnabled,
    additionalDeclarationText,
  } = project

  const [showLegalModal, setShowLegalModal] = useState(false)
  const [previewMode, setPreviewMode] = useState<PreviewMode>('mobile')

  const { teamName, logoAvatarUrl } = userProfile!

  const teamProfile = userProfile as any
  const resolvedLegal = project.legal || {}
  const hasProjectLegalOverrides = !!overrideContentOwner || !!overridePrivacyUrl || !!overrideLegalDisclosureUrl
  const usesProjectOverrides = useTeamLegalSettings === false || hasProjectLegalOverrides

  const contentOwner = usesProjectOverrides
    ? overrideContentOwner || resolvedLegal.contentOwner || teamName
    : resolvedLegal.contentOwner || teamProfile.contentOwner || teamName

  const controllerPrivacyUrl = usesProjectOverrides
    ? overridePrivacyUrl || resolvedLegal.controllerPrivacyUrl
    : resolvedLegal.controllerPrivacyUrl || teamProfile.controllerPrivacyUrl

  const legalDisclosureUrl = usesProjectOverrides
    ? overrideLegalDisclosureUrl || resolvedLegal.legalDisclosureUrl
    : resolvedLegal.legalDisclosureUrl || teamProfile.legalDisclosureUrl

  const contributorAdditionalDeclarationEnabled = usesProjectOverrides
    ? additionalDeclarationEnabled
    : resolvedLegal.additionalDeclarationEnabled ?? teamProfile.defaultAdditionalDeclarationEnabled

  const contributorAdditionalDeclarationText = usesProjectOverrides
    ? additionalDeclarationText || resolvedLegal.additionalDeclarationText
    : resolvedLegal.additionalDeclarationText || teamProfile.defaultAdditionalDeclarationText

  const { hasBrandedUploadPage } = userProfile.plan

  const hasProjectRules = !!rules
  const hasAdditionalTerms = !!contributorAdditionalDeclarationEnabled && !!contributorAdditionalDeclarationText
  const projectLanguage = locale

  const handleLegalFieldChange = (name: string, value: any) => {
    if (!handleChange) return

    handleChange(null, {
      name,
      value,
    })
  }

  const handleLegalOverrideChange = (name: keyof IProject, value: any) => {
    if (!handleChange) return

    handleChange(null, {
      name,
      value,
      projectChanges: {
        useTeamLegalSettings: false,
        [name]: value,
      },
    })
  }

  const handleAdditionalTermsChange = (value: any) => {
    if (!handleChange) return

    handleChange(null, {
      name: 'additionalDeclarationText',
      value,
      projectChanges: {
        useTeamLegalSettings: false,
        additionalDeclarationEnabled: !!value,
        additionalDeclarationText: value,
      },
    })
  }

  return (
    <>
      <Header as="h3">
        Preview (<ExternalLink url={project.projectUrl}>view live</ExternalLink>){' '}
        {!hasBrandedUploadPage && (
          <UpgradeTag
            title="Upgrade to add your branding"
            text="Upgrade to the Collector plan to apply your branding to your landing page"
          ></UpgradeTag>
        )}
      </Header>
      <div style={{ width: '100%', maxWidth: '270px', margin: '0 auto' }}>
        <Button.Group fluid size="mini" style={{ marginBottom: '0.75rem' }}>
          <Button active={previewMode === 'mobile'} onClick={() => setPreviewMode('mobile')}>
            Mobile
          </Button>
          <Button active={previewMode === 'desktop'} onClick={() => setPreviewMode('desktop')}>
            Desktop
          </Button>
        </Button.Group>

        <ClassicUploaderPreview
          project={project}
          mode={previewMode}
          teamName={teamName}
          logoAvatarUrl={logoAvatarUrl}
        />

        <div
          style={{
            width: '100%',
            boxSizing: 'border-box',
            margin: '1rem 0 0 0',
            padding: '1rem',
            border: '1px solid #d8d8d8',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <Header as="h5" style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>
            Contributor consent legals
          </Header>

          <p style={{ margin: '0 0 0.75rem 0', fontSize: '12px' }}>
            Configure the legal terms your contributors will view and agree to when making a submission.
          </p>

          <div style={{ marginBottom: '0.75rem' }}>
            <strong style={{ fontSize: '12px' }}>Your legal details</strong>
          </div>

          <ConsentStatusRow label={`Legal entity: ${contentOwner || 'Missing'}`} enabled={!!contentOwner} />
          <ConsentStatusRow label="Your Privacy Policy" enabled={!!controllerPrivacyUrl} />
          <ConsentStatusRow label="Your legal disclosure" enabled={!!legalDisclosureUrl} />

          <div style={{ margin: '0.9rem 0 0.75rem 0' }}>
            <strong style={{ fontSize: '12px' }}>Project legals</strong>
          </div>

          <ConsentStatusRow label={`Project team name: ${teamName || 'Missing'}`} enabled={!!teamName} />
          <ConsentStatusRow label={`Project language: ${projectLanguage}`} enabled={!!projectLanguage} />
          <ConsentStatusRow label="Additional terms" enabled={hasAdditionalTerms} />
          <ConsentStatusRow label="Your project rules" enabled={hasProjectRules} />
          <ConsentStatusRow label="Your Terms & Conditions" enabled={!!teamTermsUrl} />

          <div style={{ margin: '0.9rem 0 0.75rem 0' }}>
            <strong style={{ fontSize: '12px' }}>Platform legals</strong>
          </div>

          <ConsentStatusRow
            label={
              projectLanguage
                ? `Platform Privacy Notice: ${projectLanguage}`
                : 'Platform Privacy Notice: language not set'
            }
            enabled
          />
          <ConsentStatusRow label="Vloggi Platform Terms" enabled />
          <ConsentStatusRow label="Content Assignment Agreement" enabled />

          <Button basic compact fluid style={{ marginTop: '0.75rem' }} onClick={() => setShowLegalModal(true)}>
            Edit consent settings
          </Button>
        </div>
        <Modal open={showLegalModal} onClose={() => setShowLegalModal(false)} size="small">
          <Modal.Header>Contributor consent settings</Modal.Header>

          <Modal.Content scrolling>
            <div
              style={{
                background: '#f7f7f7',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
              }}
            >
              <Header as="h4" style={{ margin: '0 0 0.35rem 0' }}>
                Contributor consent settings
              </Header>
              <p style={{ margin: 0, fontSize: '13px' }}>
                These settings control what contributors are told before they upload. Vloggi platform legal terms are
                included automatically.
              </p>
            </div>

            <Form>
              <Header as="h4">Your legal details</Header>
              <p style={{ fontSize: '13px', marginTop: '-0.25rem' }}>
                These identify the organisation that owns or controls the submitted content.
              </p>

              <Form.Input
                label="Legal entity / content owner"
                name="overrideContentOwner"
                value={overrideContentOwner || contentOwner || ''}
                placeholder="Example: Vloggi Pty Ltd"
                onChange={(_event, data) => handleLegalOverrideChange('overrideContentOwner', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                The organisation that contributors assign their content rights to.
              </p>

              <Form.Input
                label="Your Privacy Policy URL"
                name="overridePrivacyUrl"
                value={overridePrivacyUrl || controllerPrivacyUrl || ''}
                placeholder="Example: https://example.com/privacy"
                onChange={(_event, data) => handleLegalOverrideChange('overridePrivacyUrl', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Add your organisation’s privacy policy so contributors know how you handle personal data.
              </p>

              <Form.Input
                label="Your legal disclosure URL"
                name="overrideLegalDisclosureUrl"
                value={overrideLegalDisclosureUrl || legalDisclosureUrl || ''}
                placeholder="Example: https://example.com/legal"
                onChange={(_event, data) => handleLegalOverrideChange('overrideLegalDisclosureUrl', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Use this for an Impressum, legal notice, corporate disclosure page or equivalent.
              </p>

              <Divider />

              <Header as="h4">Project legals</Header>
              <p style={{ fontSize: '13px', marginTop: '-0.25rem' }}>
                These apply only to this project and are shown exactly as written.
              </p>

              <Form.Select
                label="Project language"
                name="locale"
                value={locale || ''}
                placeholder="Select language"
                options={[
                  { key: 'en-gb', text: 'English UK', value: 'en-gb' },
                  { key: 'en-us', text: 'English US', value: 'en-us' },
                  { key: 'fr', text: 'French', value: 'fr' },
                  { key: 'es', text: 'Spanish', value: 'es' },
                  { key: 'de', text: 'German', value: 'de' },
                  { key: 'it', text: 'Italian', value: 'it' },
                  { key: 'nl', text: 'Dutch', value: 'nl' },
                  { key: 'pt-br', text: 'Portuguese Brazil', value: 'pt-br' },
                ]}
                onChange={(_event, data) => handleLegalFieldChange('locale', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Controls the upload page language and which Vloggi privacy notice contributors see.
              </p>

              <Form.TextArea
                label="Additional terms"
                name="additionalDeclarationText"
                value={additionalDeclarationText || ''}
                placeholder="Example: I confirm this content is my own original work and that everyone appearing has given permission."
                onChange={(_event, data) => handleAdditionalTermsChange(data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Optional extra confirmation shown as an additional contributor checkbox.
              </p>

              <Form.TextArea
                label="Project rules"
                name="rules"
                value={rules || ''}
                placeholder="Example: Videos must be filmed during the event and must not include confidential information."
                onChange={(_event, data) => handleLegalFieldChange('rules', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Free-text rules shown on the landing page. Add translated rules yourself if the project is multilingual.
              </p>

              <Form.Input
                label="Terms & Conditions URL"
                name="teamTermsUrl"
                value={teamTermsUrl || ''}
                placeholder="Example: https://example.com/terms"
                onChange={(_event, data) => handleLegalFieldChange('teamTermsUrl', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Optional project terms link shown to contributors.
              </p>

              <Divider />

              <Header as="h4">Platform legals</Header>
              <Message>
                <p>
                  Vloggi automatically includes the{' '}
                  <a href="https://vloggi.com/content-assignment-agreement/" target="_blank" rel="noopener noreferrer">
                    Content Assignment Agreement
                  </a>
                  ,{' '}
                  <a href="https://vloggi.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
                    Vloggi Platform Privacy Notice
                  </a>{' '}
                  and{' '}
                  <a href="https://vloggi.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer">
                    Vloggi Platform Terms
                  </a>
                  .
                </p>
                <p style={{ marginBottom: 0 }}>
                  The Vloggi Platform Privacy Notice is available in multiple languages and follows the project language
                  where supported.
                </p>
              </Message>
            </Form>
          </Modal.Content>

          <Modal.Actions>
            <Button onClick={() => setShowLegalModal(false)}>Done</Button>
          </Modal.Actions>
        </Modal>
      </div>
    </>
  )
}
export default CollectTabLandingPagePreview
